import { UnsureNameMark } from "@/components/molecules/unsure-name-mark";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  isReminderCancellable,
  isReminderInactive,
  REMINDER_CHANNEL_COLORS,
  reminderContent,
  reminderStatusVariant,
} from "@/features/reminders/display";
import { useCancelReminder } from "@/features/reminders/hooks/use-reminders";
import type { Reminder } from "@/features/reminders/types";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import { ChatCircleTextIcon, PhoneCallIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * A planned follow-up drawn on the calendar. Reminders are points in time, not
 * bookings, so they live in a narrow lane on the right of the day column: they never
 * compete with an appointment for space, and the difference in shape says on its own
 * that this is not something the customer is coming to.
 */
export function ReminderChip({
  reminder,
  top,
}: {
  reminder: Reminder;
  top: string;
}) {
  const { t } = useTranslation("reminders");
  const { format } = useDateFormat();
  const cancelReminder = useCancelReminder();

  const colors = REMINDER_CHANNEL_COLORS[reminder.channel];
  const Icon = reminder.channel === "sms" ? ChatCircleTextIcon : PhoneCallIcon;
  const inactive = isReminderInactive(reminder);
  const content = reminderContent(reminder);

  const handleCancel = async () => {
    try {
      const cancelled = await cancelReminder.mutateAsync(reminder.id);
      if (cancelled) {
        toast.success(t("messages.cancelSuccess"));
      } else {
        toast.error(t("messages.cancelTooLate"));
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("messages.cancelError"),
      );
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <div
          className={cn(
            "absolute right-0 z-20 flex h-3 w-[45%] cursor-pointer items-center gap-1 overflow-hidden rounded-[3px] border border-dashed px-1 hover:opacity-90",
            colors.chip,
            inactive && "opacity-60",
          )}
          style={{ top }}
          onClick={(e) => e.stopPropagation()}
          title={`${format(new Date(reminder.scheduled_at), "p")} · ${
            reminder.customer_name || t("unknownCustomer")
          } · ${reminder.reason || content.text}`}
        >
          <Icon className="h-2.5 w-2.5 shrink-0" weight="fill" />
          <span className="truncate text-[10px] font-medium leading-none">
            {reminder.customer_name || t("unknownCustomer")}
            {reminder.customer_name && reminder.unsure_customer_name && (
              <UnsureNameMark plain />
            )}
          </span>
        </div>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-80 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className={cn("h-2 w-2 rounded-full", colors.dot)} />
            <span className="font-semibold">
              {t(`channel.${reminder.channel}`)}
            </span>
          </div>
          <Badge variant={reminderStatusVariant(reminder.status)}>
            {t(`status.${reminder.status}`)}
          </Badge>
        </div>

        <div className="space-y-1 text-sm">
          <div className="font-medium">
            {reminder.customer_name || t("unknownCustomer")}
            {reminder.customer_name && reminder.unsure_customer_name && (
              <UnsureNameMark />
            )}
          </div>
          <div className="text-muted-foreground">
            {format(new Date(reminder.scheduled_at), "PPp")}
          </div>
        </div>

        {reminder.reason && (
          <div className="text-sm">
            <div className="text-xs font-medium text-muted-foreground">
              {t("fields.reason")}
            </div>
            <div className="whitespace-pre-wrap">{reminder.reason}</div>
          </div>
        )}

        {content.text && (
          <div className="text-sm">
            <div className="text-xs font-medium text-muted-foreground">
              {reminder.channel === "sms"
                ? t("fields.message")
                : t("fields.objective")}
            </div>
            <div className="whitespace-pre-wrap">{content.text}</div>
          </div>
        )}

        {/* Only worth showing when the two differ: what was promised during the call,
            next to what was actually said once it was prepared. */}
        {content.prepared && content.planned && (
          <div className="text-sm">
            <div className="text-xs font-medium text-muted-foreground">
              {t("fields.planned")}
            </div>
            <div className="whitespace-pre-wrap text-muted-foreground">
              {content.planned}
            </div>
          </div>
        )}

        {reminder.skipped_reason && (
          <div className="text-xs text-muted-foreground">
            {reminder.skipped_reason}
          </div>
        )}

        {isReminderCancellable(reminder) && (
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            disabled={cancelReminder.isPending}
            onClick={handleCancel}
          >
            {t("actions.cancel")}
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}

/** Vertical position of a reminder in a day column, on the same grid as the events. */
export const reminderTopOffset = (scheduledAt: number) => {
  const date = new Date(scheduledAt);
  return `${(date.getHours() * 4 + date.getMinutes() / 15) * 0.75}rem`;
};
